import type { AnalysisType, ComplianceAnalysisResult } from '../../shared/types';
import { C, TONES, type Tone, scoreBarHtml, toneForType, verdictStyle } from './reportTheme';

/* ════════════════════════════════════════════════════════════════════
   RAPPORT EMAIL — charte Visaly « Cream & Ink »
   ────────────────────────────────────────────────────────────────────
   Ce HTML part dans le presse-papier puis dans un client mail : tout y
   est en style inline, en table, et en hexadécimal. Deux renoncements
   assumés par rapport au PDF :

   - aucune webfont (Gmail, Outlook et consorts ne les chargent pas) —
     on reste sur une pile système, la charte tient par la couleur et
     les formes ;
   - aucun SVG inline (Gmail le supprime) — l'anneau de score devient la
     jauge segmentée `scoreBarHtml`, faite de cellules de table.
   ════════════════════════════════════════════════════════════════════ */

const FONT = "'Outfit',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif";

/* ── Inline formatting ──────────────────────────────────── */

export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function inlineMd(text: string): string {
  return esc(text)
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

export function mdToHtml(md: string): string {
  const thStyle = `text-align:left;font-weight:600;padding:8px 12px;border-bottom:2px solid ${C.line};color:${C.title};`;
  const tdStyle = `text-align:left;padding:8px 12px;border-bottom:1px solid ${C.lineSoft};vertical-align:top;color:${C.muted};`;

  // Extract tables
  const escaped = esc(md);
  const lines = escaped.split('\n');
  const tables: string[] = [];
  const processed: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const ln = lines[i].trim();
    if (ln.startsWith('|') && ln.endsWith('|') && i + 1 < lines.length) {
      const sep = lines[i + 1]?.trim() ?? '';
      if (sep.startsWith('|') && /^[\s|:-]+$/.test(sep)) {
        const hdrs = ln.slice(1, -1).split('|').map(c => c.trim());
        let t = `<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px;"><thead><tr>`;
        hdrs.forEach(h => { t += `<th style="${thStyle}">${h}</th>`; });
        t += '</tr></thead><tbody>';
        i += 2;
        while (i < lines.length) {
          const rl = lines[i].trim();
          if (!rl.startsWith('|') || !rl.endsWith('|')) break;
          const cells = rl.slice(1, -1).split('|').map(c => c.trim());
          t += '<tr>';
          cells.forEach(c => { t += `<td style="${tdStyle}">${c}</td>`; });
          t += '</tr>';
          i++;
        }
        t += '</tbody></table>';
        const ph = `__TBL${tables.length}__`;
        tables.push(t);
        processed.push(ph);
        continue;
      }
    }
    processed.push(lines[i]);
    i++;
  }

  let html = processed.join('\n')
    .replace(/^#### (.+)$/gm, `<h4 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:${C.title};">$1</h4>`)
    .replace(/^### (.+)$/gm, `<h3 style="font-size:16px;font-weight:600;margin:24px 0 10px;color:${C.title};">$1</h3>`)
    .replace(/^## (.+)$/gm, `<h2 style="font-size:18px;font-weight:600;margin:28px 0 12px;color:${C.title};">$1</h2>`)
    .replace(/^# (.+)$/gm, `<h1 style="font-size:22px;font-weight:600;margin:32px 0 14px;color:${C.ink};">$1</h1>`)
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, `<hr style="border:none;border-top:1px solid ${C.line};margin:24px 0;">`)
    .replace(/^- (.+)$/gm, '<li style="margin:6px 0;line-height:1.7;">$1</li>')
    .replace(/\n\n/g, `</p><p style="margin:14px 0;line-height:1.7;color:${C.body};">`)
    .replace(/\n/g, '<br>');

  html = html.replace(/(<li[^>]*>.*?<\/li>(?:<br>)?)+/g, (m) =>
    '<ul style="padding-left:20px;margin:14px 0;">' + m.replace(/<br>/g, '') + '</ul>',
  );

  tables.forEach((t, idx) => { html = html.replace(`__TBL${idx}__`, t); });
  return html;
}

/* ── Briques de mise en page ────────────────────────────── */

/** Pilule pleine — l'étiquette de la charte : aplat pastel, texte encre. */
function pill(label: string, bg: string, size = 12): string {
  return `<span style="display:inline-block;padding:5px 14px;border-radius:14px;background:${bg};color:${C.ink};font-size:${size}px;font-weight:600;">${esc(label)}</span>`;
}

/** Carte de section — aplat dilué de la teinte, filet de la teinte pleine.
 *  Pas de liseré latéral : la charte colore la carte entière. */
function sectionBlock(title: string, tone: Tone, count: number, content: string, lede = ''): string {
  return `
    <div style="background:${tone.wash};border:1px solid ${tone.base};border-radius:18px;padding:22px 24px;margin:20px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin-bottom:${lede ? '8px' : '16px'};"><tr>
        <td style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:${tone.deep};">${esc(title)}</td>
        ${count > 1 ? `<td align="right" style="text-align:right;"><span style="display:inline-block;padding:2px 9px;border-radius:10px;background:${tone.base};color:${C.ink};font-size:11px;font-weight:700;">${count}</span></td>` : ''}
      </tr></table>
      ${lede ? `<p style="margin:0 0 16px;font-size:12px;line-height:1.65;color:${tone.deep};">${lede}</p>` : ''}
      ${content}
    </div>`;
}

/** Cartouche de référence légale — pastille blanche cerclée de la teinte. */
function refPill(ref: string, tone: Tone): string {
  if (!ref) return '';
  return `<p style="margin:8px 0 0;"><span style="display:inline-block;padding:3px 10px;border-radius:11px;background:#ffffff;border:1px solid ${tone.soft};color:${tone.deep};font-size:11px;font-weight:600;">Réf. ${esc(ref)}</span></p>`;
}

function itemBlock(element: string, explanation: string, ref: string, tone: Tone, extra = '', badge = ''): string {
  return `
    <div style="margin-bottom:18px;">
      <p style="font-weight:600;color:${C.title};margin:0 0 5px;font-size:15px;line-height:1.5;">${inlineMd(element)}${badge}</p>
      <p style="color:${C.muted};line-height:1.75;margin:0;font-size:14px;">${inlineMd(explanation)}</p>
      ${extra}
      ${refPill(ref, tone)}
    </div>`;
}

/** Encart de recommandation — accent rose de la marque. */
function recommendation(text: string): string {
  const t = TONES.rose;
  return `<div style="margin:10px 0 0;padding:10px 14px;background:${t.wash};border-left:3px solid ${t.base};border-radius:0 10px 10px 0;">
    <span style="font-size:10px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:${t.deep};">Recommandation</span>
    <p style="margin:3px 0 0;font-size:13px;color:${t.deep};line-height:1.7;">${inlineMd(text)}</p>
  </div>`;
}

/* ── Main export ────────────────────────────────────────── */

export interface EmailReportMeta {
  /** Libellé de la typologie (« Accroche / Claim »…), affiché en pilule. */
  analysisLabel?: string;
  /** Typologie — pilote la teinte de l'en-tête. */
  analysisType?: AnalysisType | string;
}

export function buildAnalysisEmailHtml(result: ComplianceAnalysisResult, meta: EmailReportMeta = {}): string {
  const isQuestion = !!result.generalAnswer;
  const tone = toneForType(meta.analysisType ?? (isQuestion ? 'general_question' : null));
  const label = meta.analysisLabel ?? (isQuestion ? 'Question réglementaire' : 'Analyse de conformité');

  let body = '';

  if (isQuestion) {
    // Question réglementaire — la réponse markdown complète
    body += `<div style="background:${C.app};border:1px solid ${C.line};border-radius:18px;padding:26px 28px;margin:20px 0;">`;
    body += `<div style="font-size:14px;color:${C.body};line-height:1.8;">${mdToHtml(result.generalAnswer!)}</div>`;
    body += `</div>`;

    if (result.summary) {
      body += `<div style="margin:20px 0;font-size:14px;color:${C.soft};font-style:italic;line-height:1.7;">${mdToHtml(result.summary)}</div>`;
    }
  } else {
    // Analyse scorée — score, verdict, puis le trio de juges
    const v = verdictStyle(result);
    const defWins = result.judgeDefenseScore >= result.judgeContradicteurScore;

    body += `
      <div style="background:${C.app};border:1px solid ${C.line};border-radius:20px;padding:28px 24px 24px;margin:20px 0;text-align:center;">
        <div style="font-size:56px;font-weight:700;color:${C.ink};line-height:1;letter-spacing:-1.5px;">${result.score}<span style="font-size:16px;font-weight:400;color:${C.faint};letter-spacing:0;">/100</span></div>
        <div style="margin:16px 0 18px;">${scoreBarHtml(result.score)}</div>
        <div>${pill(v.label, v.tone.base, 13)}</div>
        <div style="font-size:16px;color:${C.title};margin-top:14px;font-weight:600;">${esc(v.lede)}</div>
        ${result.summary ? `<div style="margin:12px auto 0;max-width:520px;font-size:14px;color:${C.muted};line-height:1.75;">${mdToHtml(result.summary)}</div>` : ''}

        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin-top:24px;border-top:1px solid ${C.line};">
          <tr>
            <td width="33%" style="padding:18px 6px 0;text-align:center;">
              <div style="font-size:22px;font-weight:700;color:${defWins ? C.ink : C.faint};line-height:1;">${result.judgeDefenseScore}<span style="font-size:11px;font-weight:400;color:${C.faint};">/100</span></div>
              <div style="font-size:10px;letter-spacing:1.4px;text-transform:uppercase;color:${C.soft};margin-top:6px;">Juge Défense</div>
              <div style="margin:8px auto 0;width:38px;height:3px;border-radius:2px;background:${defWins ? TONES.khaki.base : C.line};font-size:0;line-height:0;">&nbsp;</div>
            </td>
            <td width="33%" style="padding:18px 6px 0;text-align:center;">
              <div style="font-size:22px;font-weight:700;color:${!defWins ? C.ink : C.faint};line-height:1;">${result.judgeContradicteurScore}<span style="font-size:11px;font-weight:400;color:${C.faint};">/100</span></div>
              <div style="font-size:10px;letter-spacing:1.4px;text-transform:uppercase;color:${C.soft};margin-top:6px;">Juge Contradicteur</div>
              <div style="margin:8px auto 0;width:38px;height:3px;border-radius:2px;background:${!defWins ? TONES.red.base : C.line};font-size:0;line-height:0;">&nbsp;</div>
            </td>
            <td width="33%" style="padding:18px 6px 0;text-align:center;">
              <div style="font-size:22px;font-weight:700;color:${C.ink};line-height:1;">${result.score}<span style="font-size:11px;font-weight:400;color:${C.faint};">/100</span></div>
              <div style="font-size:10px;letter-spacing:1.4px;text-transform:uppercase;color:${C.soft};margin-top:6px;">Arbitre</div>
              <div style="margin:8px auto 0;width:38px;height:3px;border-radius:2px;background:${v.tone.base};font-size:0;line-height:0;">&nbsp;</div>
            </td>
          </tr>
        </table>
        <p style="margin:14px 0 0;font-size:11px;color:${C.faint};line-height:1.6;">Le score final est rendu par l'arbitre — ce n'est pas une moyenne des deux juges.</p>
      </div>`;
  }

  // Hors périmètre publicité santé
  if (!isQuestion && result.outOfScopeFlags?.length) {
    const t = TONES.clay;
    const items = result.outOfScopeFlags.map(f => itemBlock(
      f.element, f.explanation, f.legalBasis, t, '',
      `<span style="display:inline-block;padding:2px 9px;border-radius:10px;background:${t.base};color:${C.ink};font-size:10px;font-weight:700;text-transform:uppercase;margin-left:8px;">${esc(f.topic)}</span>`,
    )).join('');
    body += sectionBlock(
      'Hors périmètre publicité santé', t, result.outOfScopeFlags.length, items,
      "Ces points ne relèvent pas de la réglementation de la publicité des produits de santé et n'affectent pas le score. Ils signalent un risque juridique distinct (dépôt de marque, image).",
    );
  }

  // Interdictions
  if (result.interdictionsFormelles.length > 0) {
    const t = TONES.red;
    const items = result.interdictionsFormelles.map(it => itemBlock(it.element, it.explanation, it.legalBasis, t)).join('');
    body += sectionBlock('Interdictions formelles', t, result.interdictionsFormelles.length, items);
  }

  // Points de vigilance
  if (result.pointsDeVigilance.length > 0) {
    const t = TONES.sun;
    const riskTone: Record<string, Tone> = { 'Faible': TONES.sun, 'Moyen': TONES.clay, 'Élevé': TONES.red };
    const items = result.pointsDeVigilance.map(it => {
      const rt = riskTone[it.riskLevel] ?? TONES.sun;
      const badge = `<span style="display:inline-block;padding:2px 9px;border-radius:10px;background:${rt.base};color:${C.ink};font-size:10px;font-weight:700;text-transform:uppercase;margin-left:8px;">${esc(it.riskLevel)}</span>`;
      return itemBlock(it.element, it.explanation, it.legalBasis, t, it.recommendation ? recommendation(it.recommendation) : '', badge);
    }).join('');
    body += sectionBlock('Points de vigilance', t, result.pointsDeVigilance.length, items);
  }

  // Points conformes
  if (result.conformePoints.length > 0) {
    const t = TONES.khaki;
    const items = result.conformePoints.map(it => itemBlock(it.element, it.explanation, it.legalBasis, t)).join('');
    body += sectionBlock('Points conformes', t, result.conformePoints.length, items);
  }

  // Mentions obligatoires — la checklist déterministe (R5122-3)
  const checklist = result.mentionsChecklist;
  if (!isQuestion && checklist?.applicable && (checklist.items.length > 0 || checklist.redFlags.length > 0)) {
    const t = TONES.neutral;
    const mark = (present: boolean | null) => {
      if (present === true) return `<span style="display:inline-block;width:18px;height:18px;line-height:18px;text-align:center;border-radius:9px;background:${TONES.khaki.base};color:${C.ink};font-size:11px;font-weight:700;">&#10003;</span>`;
      if (present === false) return `<span style="display:inline-block;width:18px;height:18px;line-height:18px;text-align:center;border-radius:9px;background:${TONES.red.base};color:${C.ink};font-size:11px;font-weight:700;">&#10007;</span>`;
      return `<span style="display:inline-block;width:18px;height:18px;line-height:18px;text-align:center;border-radius:9px;background:${C.card};color:${C.faint};font-size:11px;font-weight:700;">&ndash;</span>`;
    };
    const items = checklist.items.map(it => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin-bottom:12px;"><tr>
        <td width="18" valign="top" style="padding-top:2px;">${mark(it.present)}</td>
        <td style="padding-left:11px;">
          <p style="margin:0;font-size:14px;color:${C.title};line-height:1.5;">${esc(it.label)}${it.legalBasis ? `<span style="color:${C.faint};font-size:11px;margin-left:8px;">${esc(it.legalBasis)}</span>` : ''}</p>
          ${it.detail ? `<p style="margin:3px 0 0;font-size:12px;color:${C.soft};line-height:1.65;">${esc(it.detail)}</p>` : ''}
        </td>
      </tr></table>`).join('');
    const flags = checklist.redFlags.length ? `
      <div style="margin-top:16px;padding:16px 18px;background:${TONES.red.wash};border-radius:14px;">
        <p style="margin:0 0 12px;font-size:11px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:${TONES.red.deep};">Signaux d'alerte détectés</p>
        ${checklist.redFlags.map(f => `<div style="margin-bottom:12px;">
          <p style="margin:0;font-size:14px;font-weight:600;color:${C.title};line-height:1.5;">${esc(f.pattern)}</p>
          <p style="margin:2px 0 0;font-size:13px;color:${C.muted};line-height:1.7;">${esc(f.explanation)}</p>
          ${f.excerpt ? `<p style="margin:3px 0 0;font-size:13px;font-style:italic;color:${TONES.red.deep};line-height:1.6;">&laquo;&nbsp;${esc(f.excerpt)}&nbsp;&raquo;</p>` : ''}
          ${refPill(f.legalBasis, TONES.red)}
        </div>`).join('')}
      </div>` : '';
    body += sectionBlock(
      'Mentions obligatoires', t, 0, items + flags,
      `${checklist.regime ? `Régime appliqué&nbsp;: ${esc(checklist.regime)}. ` : ''}Vérification par règles, indépendante des juges IA — elle complète le verdict, elle ne le remplace pas.`,
    );
  }

  // Sources
  if (result.sources.length > 0) {
    const t = TONES.sky;
    const typeTone: Record<string, Tone> = {
      LOI: TONES.sky, JURISPRUDENCE: TONES.rose, ARPP: TONES.neutral, DOCTRINE: TONES.neutral,
    };
    const srcItems = result.sources.map(s => {
      const st = typeTone[s.type] ?? TONES.neutral;
      return `<div style="margin-bottom:9px;font-size:13px;line-height:1.6;">
        <span style="display:inline-block;padding:2px 9px;border-radius:10px;background:${st.base};color:${C.ink};font-size:10px;font-weight:700;margin-right:8px;">${esc(s.type)}</span>
        <span style="color:${C.muted};">${esc(s.title)} — ${esc(s.reference)}${s.date ? ` <span style="color:${C.faint};">(${esc(s.date)})</span>` : ''}</span>
      </div>`;
    }).join('');
    body += sectionBlock('Sources juridiques', t, 0, srcItems);
  }

  // Arbitrage
  if (!isQuestion && result.arbitrageDetails) {
    body += sectionBlock(
      "Détails de l'arbitrage", TONES.neutral, 0,
      `<div style="font-size:13px;color:${C.muted};line-height:1.8;">${mdToHtml(result.arbitrageDetails)}</div>`,
    );
  }

  const generatedAt = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:${C.cream};font-family:${FONT};">
  <div style="max-width:660px;margin:24px auto;background:${C.app};border-radius:24px;overflow:hidden;border:1px solid ${C.line};">

    <!-- En-tête : le panneau encre de la charte -->
    <div style="background:${C.ink};padding:26px 30px 28px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;"><tr>
        <td style="font-size:22px;font-weight:600;color:#ffffff;letter-spacing:-0.4px;">Visaly</td>
        <td align="right" style="text-align:right;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:rgba(255,255,255,0.55);">${esc(generatedAt)}</td>
      </tr></table>
      <div style="margin-top:22px;">${pill(label, tone.base)}</div>
      <p style="margin:16px 0 0;font-size:11px;letter-spacing:1.6px;text-transform:uppercase;color:rgba(255,255,255,0.42);">Pré-scoring réglementaire · Produits de santé</p>
    </div>

    <!-- Corps -->
    <div style="padding:8px 30px 26px;">
      ${body}
    </div>

    <!-- Pied -->
    <div style="background:${C.card};padding:22px 30px;">
      <p style="margin:0;font-size:12px;color:${C.muted};line-height:1.7;text-align:center;">
        Aide à la décision, non-opposable&nbsp;: ce pré-scoring ne remplace ni un avis juridique ni la décision de l'ANSM.<br>
        Textes cités depuis Légifrance — DILA, Licence Ouverte 2.0.
      </p>
      <p style="margin:10px 0 0;font-size:11px;color:${C.faint};text-align:center;">
        Généré par <span style="color:${C.muted};font-weight:600;">Visaly</span> · Studio Blackthorns · visaly.fr
      </p>
    </div>
  </div>
</body></html>`;
}
